import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Button,
  Card,
  InputAdornment,
  OutlinedInput,
  Table,
  TableBody,
  TableContainer,
  Toolbar,
  TablePagination,
  FormControlLabel,
  Checkbox,
  Tooltip,
  TableCell,
  Typography,
  TableRow
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTable } from '../../../../hooks/use-table';
import { Scrollbar } from '../../../core/scrollbar';
import { TableNoData } from '../../../core/table/table-no-data';
import { TableRowCompany } from './table-row-company';
import { UserTableHead } from '../../../core/table/user-table-head';
import { TableEmptyRows } from '../../../core/table/table-empty-rows';
import { emptyRows, applyFilter, getComparator } from './utils';
import { Company } from '../../../types/UserTypes';
import { Document } from '../../../types/DocumentType';
import axios from '../../../../services/ApiService';
import { useNavigate } from 'react-router-dom';
import { useDocuments } from '../../../../hooks/useDocuments';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { Iconify } from '../../../core/iconify';
import RefreshOutlineIcon from '@mui/icons-material/RefreshOutlined';
import { paths } from '../../../../utils/paths';

function AdminCompany() {
  const table = useTable();
  const navigate = useNavigate();
  const [filterName, setFilterName] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [includeInactive, setIncludeInactive] = useState<boolean>(false);


  useEffect(() => {
    fetchCompanies();
  }, [includeInactive]);

  const fetchCompanies = async () => {
    setCompanies([]);
    setLoading(true);
    try {
      const response = await axios.post('/getCompanies', includeInactive);
      if (Array.isArray(response.data)) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies.');
    } finally {
      setLoading(false);
    }
  };

  const handleIncludeInactiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeInactive(e.target.checked);
  };

  const handleCreate = useCallback(async (): Promise<void> => {
    navigate(paths.admin.newCompany, {replace: false});
  }, [navigate]);

  const dataFiltered: Company[] = applyFilter({
    inputData: companies,
    comparator: getComparator(table.order, table.orderBy),
    filterName,
  });

  const notFound = !dataFiltered.length && !!filterName;

  return (
    <>
    <Helmet><title>BVT Admin - Companies</title></Helmet>
    <div>
    <Card sx={{ mt: 2 }}>
        <Toolbar
          sx={{
            height: 86,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <OutlinedInput
              fullWidth
              value={filterName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFilterName(event.target.value);
                table.onResetPage();
              }}
              placeholder="Search Companies..."
              startAdornment={
                <InputAdornment position="start">
                  <Iconify width={20} icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
              sx={{ maxWidth: 350, maxHeight:40 }}
            />
            <Tooltip title="Include Inactive & Suspended Companies" arrow>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInactive}
                    onChange={handleIncludeInactiveChange}
                    color="primary"
                  />
                }
                label="Inactive & Suspended Companies"
                sx={{ minWidth: 350, marginLeft: 1 }}
              />
            </Tooltip>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              variant="contained"
              color="inherit"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={handleCreate}
              sx={{marginRight: 1}}
            >
              New Company
            </Button>
            <Tooltip title="Reload Companies" arrow>
              <Button
                variant="contained"
                color="inherit"
                onClick={() => fetchCompanies()}
              >
                <RefreshOutlineIcon />
              </Button>
          </Tooltip>
          </Stack>
        </Toolbar>
        <Scrollbar>
          <TableContainer sx={{ overflow: 'unset' }}>
            <Table sx={{ minWidth: 800 }}>
              <UserTableHead
                order={table.order}
                orderBy={table.orderBy}
                onSort={table.onSort}
                headLabel={[
                  { id: 'companyName', label: 'Company Name' },
                  { id: 'abn', label: 'ABN' },
                  { id: 'location', label: 'Office Location' },
                  { id: 'contact', label: 'Contact' },
                  { id: 'isVerified', label: 'Verified', align: 'center' },
                  { id: 'isDefault', label: 'Default', align: 'center' },
                  { id: 'status', label: 'Status', align: 'center' }
                ]}
              />
              <TableBody>
                {dataFiltered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="h6" color="text.secondary">
                        {loading ? '' : 'No Companies Found for Given Criteria'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {dataFiltered
                      .slice(
                        table.page * table.rowsPerPage,
                        table.page * table.rowsPerPage + table.rowsPerPage
                      )
                      .map((row) => (
                        <TableRowCompany
                          key={row.companyId}
                          row={row}
                        />
                      ))}

                    <TableEmptyRows
                      height={68}
                      emptyRows={emptyRows(table.page, table.rowsPerPage, companies.length)}
                    />
                  </>
                )}

                {notFound && <TableNoData searchQuery={filterName} />}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          component="div"
          page={table.page}
          count={companies.length}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          rowsPerPageOptions={[5, 10, 25]}
          onRowsPerPageChange={table.onChangeRowsPerPage}
        />
      </Card>
    </div>
    <LoadingSpinner loading={loading} />
    </>
  )
}

export default AdminCompany;